import AbstractGenerator from '../../../misc/generator';
import AbstractObject from '../../../misc/object';

import Ground from '../objects/ground';
import Panel from '../../../objects/panel';
import Barrier from '../objects/barrier';
import Arrow from '../../../objects/arrow';
import { HTMLEditableLabel } from '../../../objects/html-label';
import { addDistanceLabel, getEndpoints } from '../../../../services/helper.service';
import { unitTransform } from '@services/helper.service';
import { defaultUnits } from '@config';

import Scene from "@scene";
import { LabelsPadding } from '@scene/objects/ground';

import { generateScrewGroup } from './helper';
import * as THREE from 'three';
import { ScrewGroup } from '@scene/objects/screw';
import Line from '@scene/objects/line';

export default class Stairs extends AbstractGenerator {

  constructor(options, dispatcher) {
    super(options);

    if (!this.options.ground) throw new Error('Ground options is required');
    if (!this.options.barrier) throw new Error('Barrier options is required');
    if (!this.options.panel) throw new Error('Panel options is required');

    this.dispatchOptionUpdate = dispatcher;

    this.options.ground.type = Ground.types.STAIRS;
    this.options.barrier.type = Barrier.types.STAIRS;
    this.panels = new AbstractObject();
    if (this.options.screw.count.n === 3) {
      this.options.panel.circleCorrection = 90;
    }
  }

  init3DObject() {
    this.layer3D.addObject(this.panels);

    const STAY_COMMON_DPOT = (this.options.barrier.dpot * (this.options.barrier.stayCount - 1));
    const stepsBetweenStays = STAY_COMMON_DPOT / this.options.ground.stairs.width;
    this.options.ground.stairs.count = Math.floor(stepsBetweenStays) + 1;

    this.createGround();
    this.layer3D.addObject(this.ground);

    if (this.ground.size.step.width <= this.options.barrier.dpot) {
      this.options.barrier.tilt = this.ground.size.height / (this.options.barrier.stayCount - 1) - this.options.ground.stairs.height/2;
    } else {
      this.options.barrier.tilt = 0;
    }

    this.barrier = new Barrier(this.options.barrier);
    this.barrier.mesh.rotation.y = Math.PI / 2;
    this.layer2D.addObject(this.barrier);

    let barrierXPosition = this.panels.position.x;

    switch (this.options.panel.position) {
      case Panel.positions.HORIZONTAL_X: {
        this.barrier.staysPositions.forEach((item, index) => {
          const panel = new Panel({
            ...this.options.panel//,
            //screw: this.options.screw
          });
          panel.mesh.rotation.z = Math.PI / 2;
          panel.onOptionUpdate(this.dispatchOptionUpdate);
          panel.mesh.position.set(item.z, item.y, -item.x);

          panel.mesh.rotation.x = 0;
          panel.mesh.rotation.y = Math.PI / 2;
          panel.mesh.rotation.z = Math.PI / 2;

          this.panels.object.addObject(panel);

          if (index === 1) {
            this.middlePanel = panel;
            this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
          } else {
            generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
          }
        });

        this.screwPositions = getEndpoints(this.screwGroup.screwCoordinates);

        //VladP: Keep the first post in the right position based on the edges and screws
        const offsetX = -this.ground.size.width / 2 + (this.options.barrier.stayCount - 1) /2 * this.options.barrier.dpot;
        this.panels.position.set(
          this.ground.position.x + this.ground.size.depth 
          - (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2) 
          - (this.options.ground.padding.C1y || 0) + (this.options.panel.dy ? this.options.panel.dy.value : 0),
          this.ground.position.y + this.ground.size.tst + this.ground.size.step.height / 2 + this.options.panel.depth / 2,
          this.ground.position.z -offsetX - (this.options.ground.padding.C1x || 0) - this.options.panel.width / 2 +
            (this.options.panel.width / 2 - this.screwPositions.left.y)
        );

        barrierXPosition = this.panels.position.x;

        // Create screw labels
        const screwLabels = this.screwGroup.getScrewLabels();
        screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
          htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
          // htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2);
          htmlElem.position.add(this.panels.mesh.position);
          htmlElem.optimalCameraDist = 3000;
          htmlElem.zoomFactor = 0.5;
        });
        this.layer3D.addObject(screwLabels);
        break;
      }
      case Panel.positions.VERTICAL_X: {
        this.barrier.staysPositions.forEach((item, index) => {
          const panel = new Panel({
            ...this.options.panel//,
            //screw: this.options.screw
          });
          panel.onOptionUpdate(this.dispatchOptionUpdate);
          panel.mesh.position.set(item.z, item.y, -item.x);

          this.panels.object.addObject(panel);

          if (index === 1) {
            this.middlePanel = panel;
            this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
          } else {
            generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
          }
        });

        this.screwPositions = getEndpoints(this.screwGroup.screwCoordinates);
          
        //VladP: Keep the first post in the right position based on the edges and screws
        const PADDING_TOP = this.options.panel.type === Panel.types.BEND ? 0 : this.DPP;
        const offsetX = -this.ground.size.width / 2 + (this.options.barrier.stayCount - 1) /2 * this.options.barrier.dpot;
        this.panels.position.set(
          this.ground.position.x + this.ground.size.depth + this.options.panel.depth / 2,
          this.ground.position.y + this.ground.size.tst + this.ground.size.step.height / 2 - PADDING_TOP
          - (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2),
          this.ground.position.z - offsetX - this.options.panel.width / 2 - (this.options.panel.dpl.value || 0)
        );

        if (this.options.panel.type === Panel.types.BEND) {
          this.panels.position.y += this.options.panel.depth;
        }

        barrierXPosition = this.panels.position.x + this.options.panel.depth / 2 + this.barrier.size.depth / 2;

        // Create screw labels
        const screwLabels = this.screwGroup.getScrewLabels();
        screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
          htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
          htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2);
          htmlElem.position.add(this.panels.mesh.position);
          htmlElem.optimalCameraDist = 3000;
          htmlElem.zoomFactor = 0.5;
        });
        this.layer3D.addObject(screwLabels);
        break;
      }
      default: {
        break;
      }
    }

    const BARRIER_BOTTOM = this.barrier.size.height;
    this.barrier.position.set(
      barrierXPosition,
      this.panels.position.y + BARRIER_BOTTOM + this.barrier.size.height / -2,
      this.panels.position.z
    );
    switch (this.options.panel.position) {
      case Panel.positions.HORIZONTAL_X: { 
        this.barrier.mesh.position.z -= this.options.beam.shift.x;
        this.barrier.mesh.position.x -= this.options.beam.shift.y;
        break;
      }
      case Panel.positions.VERTICAL_X: {
        this.barrier.mesh.position.z -= this.options.beam.shift.x;
        this.barrier.mesh.position.y += this.options.beam.shift.y;
        break;
      }
      default:
        break;
    }
    this.layer3D.addObject(this.barrier);

    if (this.options.object.dimensions.display || this.options.object.dimensions.renderForReport) {
      // display on the report regardless of the display setting

      // Create dpot label
      const dpot = this.barrier.createDpotLabel(this.dispatchOptionUpdate, this.options.object.dimensions);
      this.layer3D.addObject(dpot.arrow);
      this.layer3D.htmlLayer.add(dpot.htmlElement);

      // Create hgs label
      const hgs = this.barrier.createHgsLabel(this.dispatchOptionUpdate, this.options.object.dimensions);
      this.layer3D.addMesh(hgs.arrow);
      this.layer3D.htmlLayer.add(hgs.htmlElement);
    }
    if (this.options.object.dimensions.display) {
      this.addLabels();

      if (this.options.ground.stairs.count >= 2) {
        // Create hst label
        const hst = this.ground.createHstLabel(this.dispatchOptionUpdate, this.options.object.dimensions);
        hst.htmlElement.optimalCameraDist = 3000;
        hst.htmlElement.zoomFactor = 0.5;
        if (!this.options.object.dimensions.displayHorizontally) {
          hst.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
          hst.htmlElement.position.z += 15;
          hst.htmlElement.setDirection(
            hst.htmlElement.position.clone().setY(
              hst.htmlElement.position.y + Math.max(25, this.ground.size.step.height / 2)),
              hst.htmlElement.position.clone().setY(
                hst.htmlElement.position.y - Math.max(25, this.ground.size.step.height / 2)),
            false
          );
        }
        this.layer3D.addObject(hst.arrow);
        this.layer3D.htmlLayer.add(hst.htmlElement);

        // Create lst label
        const lst = this.ground.createLstLabel(this.dispatchOptionUpdate, this.options.object.dimensions);
        lst.htmlElement.optimalCameraDist = 3000;
        lst.htmlElement.zoomFactor = 0.5;
        if (!this.options.object.dimensions.displayHorizontally) {
          lst.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
          lst.htmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
          lst.htmlElement.position.x += 15;
          lst.htmlElement.setDirection(
            lst.htmlElement.position.clone().setZ(
              lst.htmlElement.position.z + Math.max(25, this.ground.size.step.width / 2)),
              lst.htmlElement.position.clone().setZ(
                lst.htmlElement.position.z - Math.max(25, this.ground.size.step.width / 2)),
            false
          );
        }
        this.layer3D.addObject(lst.arrow);
        this.layer3D.htmlLayer.add(lst.htmlElement);
      }

      if (this.options.ground.stairs.tst) {
        // Create tst label
        const tst = this.ground.createTstLabel(this.dispatchOptionUpdate, this.options.object.dimensions);
        tst.htmlElement.optimalCameraDist = 3000;
        tst.htmlElement.zoomFactor = 0.5;
        this.layer3D.addMesh(tst.arrow);
        this.layer3D.htmlLayer.add(tst.htmlElement);
      }
    }
  }

  init2DObject() {

      this.options.barrier.type = Barrier.types.STAIRS;

      this.layer2D.addObject(this.panels);

      this.options.ground.stairs.count = 1;

      this.createGround();
      this.layer2D.addObject(this.ground);

      if (this.ground.size.step.width <= this.options.barrier.dpot) {
        this.options.barrier.tilt = this.ground.size.height / (this.options.barrier.stayCount - 1);
      } else {
        this.options.barrier.tilt = 0;
      }

      this.barrier = new Barrier({
        ...this.options.barrier,
        stayCount: 1,
        disableTop: true
      });
      //  this.barrier.mesh.rotation.y = -Math.PI / 2;

      // Create panel object
      const panel = new Panel({
        ...this.options.panel//,
        //screw: this.options.screw
      });
      panel.onOptionUpdate(this.dispatchOptionUpdate);
      const panelPosition = this.barrier.staysPositions[0];
      panel.mesh.position.set(panelPosition.z, panelPosition.y, -panelPosition.x);
      // TODO: Hot fix
      this.layer2D.htmlLayer.addLayer(panel.htmlLayer);
      this.middlePanel = panel;
      this.panels.object.addObject(panel);

      this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
      
      this.screwPositions = getEndpoints(this.screwGroup.screwCoordinates);
      let barrierXPosition = this.panels.position.x;
      const panelHeight = (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width : this.options.panel.height);
      let htmlLayerOffset = new THREE.Vector3();
      switch (this.options.panel.position) {
        case Panel.positions.HORIZONTAL_X: {
          this.ground.ground.position.x = (this.options.ground.padding.C1y || 0) + panelHeight / 2 - 
            (this.options.panel.dy ? this.options.panel.dy.value : 0);
          this.ground.ground.position.y = (this.options.ground.padding.C1x || 0) + this.options.panel.width / 2 - 
            (this.options.panel.width / 2 - this.screwPositions.left.y);

          this.ground.mesh.rotation.z = Math.PI / -2;
          this.ground.mesh.rotation.x = Math.PI / -2;

          barrierXPosition = this.panels.position.x;

          panel.mesh.rotation.x = 0;
          panel.mesh.rotation.y = 0;
          panel.mesh.rotation.z = Math.PI / 2;

          break;
        }
        case Panel.positions.VERTICAL_X: {
          const PADDING_TOP = this.options.panel.type === Panel.types.BEND ? this.options.panel.depth : -this.DPP;

          this.panels.position.set(
            this.ground.position.x + this.ground.size.depth + this.options.panel.depth / 2,
            this.ground.position.y + this.ground.size.tst + this.ground.size.step.height + PADDING_TOP - panelHeight / 2,
            this.ground.position.z - this.options.panel.width / 2 - this.options.panel.dpl.value || 0
          );
          htmlLayerOffset.x = -this.panels.position.z;
          htmlLayerOffset.z = -this.panels.position.y;
          barrierXPosition = this.panels.position.x + this.options.panel.depth;
          this.barrier.mesh.rotation.y = -Math.PI / 2;

          break;
        }
        default: {
          break;
        }
      }

      const BARRIER_BOTTOM = this.barrier.size.height;
      this.barrier.position.set(
        barrierXPosition,
        this.panels.position.y + BARRIER_BOTTOM + this.barrier.size.height / -2,
        this.panels.position.z
      );
      switch (this.options.panel.position) {
          case Panel.positions.HORIZONTAL_X: { 
            this.barrier.mesh.position.x += this.options.beam.shift.x;
            this.barrier.mesh.position.z -= this.options.beam.shift.y;
            break;
          }
          case Panel.positions.VERTICAL_X: {
            this.barrier.mesh.position.z -= this.options.beam.shift.x;
            this.barrier.mesh.position.y += this.options.beam.shift.y;
            break;
          }
          default:
            break;
        }
      this.layer2D.addObject(this.barrier);

    if (this.options.panel.margin && this.options.panel.margin.top) {
      this.panels.mesh.position.y += this.options.panel.margin.top;
    }

    const { compressionLine } = panel.getCompressionLine();
    if (compressionLine) {
      compressionLine.mesh.rotation.copy(panel.rotation);
      compressionLine.mesh.position.copy(this.panels.position);
      this.layer2D.addObject(compressionLine);
      let zonesLayer = panel.createZonesLabels();
      if (zonesLayer) {
        zonesLayer.mesh.rotation.copy(panel.rotation);
        zonesLayer.mesh.position.copy(this.panels.position);
        zonesLayer.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
          htmlElem.position.x += htmlLayerOffset.x;
          htmlElem.position.y += htmlLayerOffset.y;
          htmlElem.position.z += htmlLayerOffset.z;
        });
        this.layer2D.addLayer(zonesLayer);
      }
    }

    this.addLabels();

    // mask on camera
    this.barrier.mesh.ignoreOnCameraReset = true;
    this.barrier.mesh.traverseVisible(function (child) {
      if (child instanceof THREE.Mesh) {
        child.ignoreOnCameraReset = true;
      }
    });
    this.ground.mesh.ignoreOnCameraReset = true;
    this.ground.mesh.traverseVisible(function (child) {
      if (child instanceof THREE.Mesh) {
        child.ignoreOnCameraReset = true;
      }
    });
  }

  addLabels() {
    const layer = this.is3DMode ? this.layer3D : this.layer2D;
    let panelHTMLLabelsCorrection = 0;
    if (this.options.panel.margin && this.options.panel.margin.top) {
      panelHTMLLabelsCorrection += this.options.panel.margin.top;
    }
    let options = null;
    let screwLabels = null;
    if (this.is3DMode) {
      options = {
        is3DMode: true,
        // rotation: new THREE.Euler(Math.PI / 2, 0, 0), //this.layer3D.mesh.rotation,
        showLabels: this.options.object.dimensions.showLabels,
        displayHorizontally: this.options.object.dimensions.displayHorizontally,
        isSafetyBarrier: true
      };
    } else if (this.is2DMode) {
      screwLabels = this.screwGroup.getScrewLabels();
      layer.addObject(screwLabels);
    }

    const panelHeight = (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width : this.options.panel.height);
    switch (this.options.panel.position) {
      case Panel.positions.HORIZONTAL_X: {
        // Create label for Dy
        const dyLabel = this.screwGroup.createDyLabel(null, options);
        if (this.is3DMode) {
          dyLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
          dyLabel.htmlElement.position.set(
            this.panels.position.x + dyLabel.arrow.position.z - this.middlePanel.options.dy.value / 2, 
            this.panels.position.y + dyLabel.arrow.position.x + this.middlePanel.size.depth / 2,
            this.panels.position.z + dyLabel.arrow.position.y + 20
          );
          dyLabel.htmlElement.optimalCameraDist = 3000;
          dyLabel.htmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            // dyLabel.htmlElement.position.z += 20;
            let dyHtmlDist = Math.max(25, this.middlePanel.options.dy.value / 2);
            dyLabel.htmlElement.setDirection(
              dyLabel.htmlElement.position.clone().setX(dyLabel.htmlElement.position.x + dyHtmlDist),
              dyLabel.htmlElement.position.clone().setX(dyLabel.htmlElement.position.x - dyHtmlDist),
              false
            );
          }
        } else {
          dyLabel.htmlElement.position.set(
            this.panels.position.x - dyLabel.arrow.position.y,
            this.panels.position.y,
            this.panels.position.z + dyLabel.arrow.position.z - this.middlePanel.options.dy.value / 2
          );
        }
        layer.htmlLayer.add(dyLabel.htmlElement);

        // Create depth label for panel
        let panelDepth = null;
        if (this.is3DMode) {
          panelDepth = this.middlePanel.create3DDepthLabel(options);
          panelDepth.object.rotation.z = Math.PI; // move on the other side to prevent having the label under stairs
          this.middlePanel.mesh.add(panelDepth.object);
          panelDepth.htmlElement.align = HTMLEditableLabel.types.align.RIGHT;
          panelDepth.htmlElement.position.set(
            this.panels.position.x + panelDepth.htmlElement.position.x,
            this.panels.position.y,
            this.panels.position.z - panelDepth.htmlElement.position.z,
          );
          panelDepth.htmlElement.optimalCameraDist = 3000;
          panelDepth.htmlElement.zoomFactor = 0.5;
        } else {
          panelDepth = this.middlePanel.createDepthLabel();
          panelDepth.htmlElement.position.set(
            this.panels.position.x - panelDepth.path[1].y,
            this.panels.position.y,
            this.panels.position.z + panelDepth.path[1].z,
          );
        }
        layer.htmlLayer.add(panelDepth.htmlElement);

        // Create width label
        const panelWidth = this.middlePanel.createWidthLabel(options);
        this.middlePanel.object.addMesh(panelWidth.line);
        if (this.is3DMode) {
          panelWidth.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
          panelWidth.htmlElement.position.set(
            this.panels.position.x + panelWidth.htmlElement.position.x,
            this.panels.position.y, 
            this.panels.position.z,
          );
          panelWidth.htmlElement.optimalCameraDist = 3000;
          panelWidth.htmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            let panelWidthHtmlDist = Math.max(25, this.middlePanel.size.width / 2);
            panelWidth.htmlElement.setDirection(
              panelWidth.htmlElement.position.clone().setZ(panelWidth.htmlElement.position.z + panelWidthHtmlDist),
              panelWidth.htmlElement.position.clone().setZ(panelWidth.htmlElement.position.z - panelWidthHtmlDist),
              false
            );
          }
        } else {
          panelWidth.htmlElement.position.x += this.panels.position.x;
          panelWidth.htmlElement.position.z += this.panels.position.z;
        }
        layer.htmlLayer.add(panelWidth.htmlElement);

        // For not CIRCLE panel type
        if (this.middlePanel.options.type !== Panel.types.CIRCLE) {
          // Create height label
          const panelHeight = this.middlePanel.createHeightLabel(options);
          this.middlePanel.object.addMesh(panelHeight.line);
          if (this.is3DMode) {
            panelHeight.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            panelHeight.htmlElement.position.set(
              this.panels.position.x, 
              this.panels.position.y,
              this.panels.position.z - panelHeight.htmlElement.position.x - 20
            );
            panelHeight.htmlElement.optimalCameraDist = 3000;
            panelHeight.htmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              // heightHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              let panelHeightHtmlDist = Math.max(25, this.middlePanel.size.height / 2);
              panelHeight.htmlElement.setDirection(
                panelHeight.htmlElement.position.clone().setX(panelHeight.htmlElement.position.x + panelHeightHtmlDist),
                panelHeight.htmlElement.position.clone().setX(panelHeight.htmlElement.position.x - panelHeightHtmlDist),
                false
              );
            }
          } else {
            panelHeight.htmlElement.position.x += this.panels.position.x;
            panelHeight.htmlElement.position.z += this.panels.position.z;
          }
          layer.htmlLayer.add(panelHeight.htmlElement);
        } else if (this.middlePanel.options.type === Panel.types.CIRCLE) {
          const screwCircle = this.screwGroup.createScrewCircle(options);
          this.middlePanel.object.addMesh(screwCircle.object);
          if (this.is3DMode) {
            screwCircle.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            screwCircle.htmlElement.verticalAlign = HTMLEditableLabel.types.align.CENTER;
            screwCircle.htmlElement.position.set(
              this.panels.position.x + screwCircle.htmlElement.position.x, 
              this.panels.position.y + this.middlePanel.options.depth / 2,
              this.panels.position.z - screwCircle.htmlElement.position.z
            );
            screwCircle.htmlElement.optimalCameraDist = 3000;
            screwCircle.htmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              // circleHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              screwCircle.htmlElement.setDirection(
                screwCircle.htmlElement.position.clone().setZ(
                  screwCircle.htmlElement.position.z + Math.max(25, this.screwGroup.options.padding.diameter / 2)),
                screwCircle.htmlElement.position.clone().setZ(
                  screwCircle.htmlElement.position.z - Math.max(25, this.screwGroup.options.padding.diameter / 2)),
                false
              );
            }
          } else {
            screwCircle.htmlElement.position.x += this.panels.position.x;
            screwCircle.htmlElement.position.z += this.panels.position.z;
          }
          layer.htmlLayer.add(screwCircle.htmlElement);
        }

        if (this.middlePanel.options.type === Panel.types.SQUARE) {
          if (this.options.screw.count.n > 1) {
            // Create S1 label
            const { object: s1Object, htmlElement: s1HtmlElement } = this.middlePanel.createS1Label(
              this.screwGroup,
              false, 
              this.ground.options.padding.C1y + LabelsPadding,
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options
            );
            if (s1HtmlElement && s1Object) {
              if (this.is3DMode) {
                s1HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
                s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
                s1HtmlElement.position.set(
                  this.panels.position.x + s1HtmlElement.position.z + 15, 
                  this.panels.position.y + this.middlePanel.size.depth / 2,
                  this.panels.position.z
                );
                s1HtmlElement.optimalCameraDist = 3000;
                s1HtmlElement.zoomFactor = 0.5;
                if (!options.displayHorizontally) {
                  s1HtmlElement.setDirection(
                    s1HtmlElement.position.clone().setZ(
                      s1HtmlElement.position.z + Math.max(25, this.screwGroup.options.padding.s1 / 2)),
                    s1HtmlElement.position.clone().setZ(
                      s1HtmlElement.position.z - Math.max(25, this.screwGroup.options.padding.s1 / 2)),
                    false
                  );
                }
              } else {
                s1HtmlElement.position.x += this.panels.position.x;
                s1HtmlElement.position.z += this.panels.position.z;
              }
              this.middlePanel.object.addMesh(s1Object);
              layer.htmlLayer.add(s1HtmlElement);
            }
          }

          if (this.options.screw.count.m > 1 || this.options.screw.formation === ScrewGroup.formation.TRIANGLE) {
            // Create S2 label
            const { object: s2Object, htmlElement: s2HtmlElement } = this.middlePanel.createS2Label(
              this.screwGroup,
              true, 
              this.middlePanel.size.width / 2 + LabelsPadding,
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options 
            );
            if (s2HtmlElement && s2Object) {
              if (this.is3DMode) {
                s2HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
                s2HtmlElement.position.set(
                  this.panels.position.x + s2HtmlElement.position.z, 
                  this.panels.position.y + s2HtmlElement.position.y,
                  this.panels.position.z - s2HtmlElement.position.x + 20
                );
                s2HtmlElement.optimalCameraDist = 3000;
                s2HtmlElement.zoomFactor = 0.5;
                if (!options.displayHorizontally) {
                  // s2HtmlElement.position.z += 20;
                  s2HtmlElement.setDirection(
                    s2HtmlElement.position.clone().setX(
                      s2HtmlElement.position.x + Math.max(25, this.screwGroup.options.padding.s2 / 2)),
                    s2HtmlElement.position.clone().setX(
                        s2HtmlElement.position.x - Math.max(25, this.screwGroup.options.padding.s2 / 2)),
                    false
                  );
                }
              } else {
                s2HtmlElement.position.x += this.panels.position.x;
                s2HtmlElement.position.z += this.panels.position.z;
              }
              this.middlePanel.object.addMesh(s2Object);
              layer.htmlLayer.add(s2HtmlElement);
            }
          }
        }

        const { line: c1yLine, htmlElement: c1yHtmlElement } = this.createC1yLabel(
          panelHeight / 2 - this.middlePanel.options.dy.value, 
          options
        );
        layer.htmlLayer.add(c1yHtmlElement);
        layer.addMesh(c1yLine);

        const { line: c1xLine, htmlElement: c1xHtmlElement } = this.createC1xLabel(
          panelHeight / 2 - this.middlePanel.options.dy.value,
          options
        );
        layer.htmlLayer.add(c1xHtmlElement);
        layer.addMesh(c1xLine);
        break;
      }
      case Panel.positions.VERTICAL_X: {
        if (this.is2DMode) {
          screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
            htmlElem.position.x -= this.panels.position.z;
            // htmlElem.position.y += this.panels.position.x;
            htmlElem.position.z -= this.panels.position.y + (this.options.panel.type === Panel.types.BEND ? 
              this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
          });
        }

        // Create dpl label
        const dplLabel = this.createDplLabel(
          {
          x: this.panels.position.x,
          y: this.panels.position.y + panelHeight / 2 + LabelsPadding + panelHTMLLabelsCorrection,
          z: this.panels.position.z + this.middlePanel.size.width / 2
          },
          options
        );
        
        if (this.is2DMode) {
          dplLabel.htmlElement.position.set(
            -dplLabel.htmlElement.position.z,
            dplLabel.htmlElement.position.y,
            dplLabel.htmlElement.position.x
          );
        } else {
          dplLabel.htmlElement.position.set(
            this.panels.position.x,
            this.panels.position.y - panelHTMLLabelsCorrection + LabelsPadding + 
              (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2),
            this.panels.position.z + this.middlePanel.size.width / 2 + this.options.panel.dpl.value / 2 
          );
          dplLabel.htmlElement.optimalCameraDist = 3000;
          dplLabel.htmlElement.zoomFactor = 0.5;
          
          if (!options.displayHorizontally) {
            dplLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            // dplLabel.htmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            // dplLabel.htmlElement.position.y += 15;
            dplLabel.htmlElement.setDirection(
              dplLabel.htmlElement.position.clone().setZ(dplLabel.htmlElement.position.y + this.options.panel.dpl.value / 2),
              dplLabel.htmlElement.position.clone().setZ(dplLabel.htmlElement.position.y - this.options.panel.dpl.value / 2),
              false
            );
          }
        }
        layer.mesh.add(dplLabel.line);
        layer.htmlLayer.add(dplLabel.htmlElement);

        // Create label for Dy
        const { htmlElement, arrow } = this.screwGroup.createDyLabel(null, options);
        if (this.is2DMode) {
          htmlElement.position.set(
            -this.panels.position.z - arrow.position.y,
            this.panels.position.x,
            -this.panels.position.y + arrow.position.z - this.middlePanel.options.dy.value / 2 - panelHTMLLabelsCorrection - 
              (this.options.panel.type === Panel.types.BEND ? 
                this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0)
          );
        } else {
          htmlElement.position.set(
            this.panels.position.x + this.middlePanel.size.depth / 2,
            this.panels.position.y + this.middlePanel.options.dy.value / 2 - panelHTMLLabelsCorrection -
              (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2),
            this.panels.position.z + arrow.position.y
          );
          htmlElement.optimalCameraDist = 3000;
          htmlElement.zoomFactor = 0.5;
          
          if (!options.displayHorizontally) {
            htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            htmlElement.position.z += 15;
            htmlElement.setDirection(
              htmlElement.position.clone().setY(htmlElement.position.y + this.middlePanel.options.dy.value / 2),
              htmlElement.position.clone().setY(htmlElement.position.y - this.middlePanel.options.dy.value / 2),
              false
            );
          }
        }
        layer.htmlLayer.add(htmlElement);

        // Create Dpp label
        if (this.options.panel.dpp.active) {
          const dppLabel = this.createDppLabel(this.middlePanel, options);
          if (this.is3DMode) {
            // dppLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
            dppLabel.htmlElement.position.set(
              this.panels.position.x,
              this.panels.position.y + this.options.panel.dpp.value / 2 + 
              (this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width / 2 : this.options.panel.height / 2),
              this.panels.position.z - this.options.panel.width / 2 - LabelsPadding
            );
            dppLabel.htmlElement.optimalCameraDist = 3000;
            dppLabel.htmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              dppLabel.htmlElement.align = HTMLEditableLabel.types.align.CENTER;
              dppLabel.htmlElement.position.z -= 15;
              dppLabel.htmlElement.setDirection(
                dppLabel.htmlElement.position.clone().setY(
                  dppLabel.htmlElement.position.y + Math.max(25, this.options.panel.dpp.value / 2)),
                dppLabel.htmlElement.position.clone().setY(
                  dppLabel.htmlElement.position.y - Math.max(25, this.options.panel.dpp.value / 2)),
                false
              );
            }
          }
          layer.htmlLayer.add(dppLabel.htmlElement);
        }

        // Create depth label for panel
        let panelDepth;
        if (this.is3DMode) {
          if (this.middlePanel.type === Panel.types.BEND) {
            panelDepth = this.middlePanel.create3DDepthLabelForBend(options);
            layer.mesh.add(panelDepth.object);
          } else {
            panelDepth = this.middlePanel.create3DDepthLabel(options);
            panelDepth.object.rotation.y = -Math.PI; // move on the other side to prevent overlap with DPP label
            this.middlePanel.mesh.add(panelDepth.object);
            panelDepth.htmlElement.align = HTMLEditableLabel.types.align.LEFT;
            panelDepth.htmlElement.position.y = this.panels.position.y + panelDepth.htmlElement.position.x + panelHTMLLabelsCorrection;
            panelDepth.htmlElement.position.x = this.panels.position.x;
            panelDepth.htmlElement.position.z = this.panels.position.z + panelDepth.htmlElement.position.z;
          }
          panelDepth.htmlElement.optimalCameraDist = 3000;
          panelDepth.htmlElement.zoomFactor = 0.5;
        } else {
          panelDepth = this.middlePanel.createDepthLabel();
          panelDepth.htmlElement.position.set(
            -this.panels.position.z - panelDepth.path[1].y,
            this.panels.position.x,
            -this.panels.position.y + panelDepth.path[1].z - panelHTMLLabelsCorrection
          );
        }
        layer.htmlLayer.add(panelDepth.htmlElement);

        // Create width label
        const { line: widthLine, htmlElement: widthHtmlElement } = this.middlePanel.createWidthLabel(options);
        this.middlePanel.object.addMesh(widthLine);
        if (this.is2DMode) {
          widthHtmlElement.position.x -= this.panels.position.z + (this.options.panel.type === Panel.types.BEND ? 
            this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
          widthHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
        } else {
          widthHtmlElement.position.set(
            this.panels.position.x,
            this.panels.position.y - panelHTMLLabelsCorrection + LabelsPadding + 
              (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2),
            this.panels.position.z
          );
          widthHtmlElement.optimalCameraDist = 3000;
          widthHtmlElement.zoomFactor = 0.5;
          if (!options.displayHorizontally) {
            widthHtmlElement.setDirection(
              widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z + this.middlePanel.size.width / 2),
              widthHtmlElement.position.clone().setZ(widthHtmlElement.position.z - this.middlePanel.size.width / 2),
              false
            );
          }
          widthHtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.BOTTOM;
        }
        layer.htmlLayer.add(widthHtmlElement);

        // For not CIRCLE panel type
        if (this.middlePanel.options.type !== Panel.types.CIRCLE) {
          // Create Ly (height) label
          const { line: heightLine, htmlElement: heightHtmlElement } = this.middlePanel.createHeightLabel(options);
          if (this.is2DMode) {
            heightHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
            heightHtmlElement.position.x -= this.panels.position.z + (this.options.panel.type === Panel.types.BEND ? 
              this.options.panel.depth / 2 - this.screwGroup.screws[0].options.radius / 2 - 1 : 0);
          } else {
            heightHtmlElement.position.set(
              this.panels.position.x,
              this.panels.position.y,
              this.panels.position.z - this.middlePanel.size.width / 2 - LabelsPadding
              );
              heightHtmlElement.optimalCameraDist = 3000;
              heightHtmlElement.zoomFactor = 0.5;
              if (!options.displayHorizontally) {
              heightHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              heightHtmlElement.position.z -= 15;
              heightHtmlElement.setDirection(
                heightHtmlElement.position.clone().setY(heightHtmlElement.position.y + this.middlePanel.size.height / 2),
                heightHtmlElement.position.clone().setY(heightHtmlElement.position.y - this.middlePanel.size.height / 2),
                false
                );
              } else {
                heightHtmlElement.align = HTMLEditableLabel.types.align.LEFT;
              }
          }
          this.middlePanel.object.addMesh(heightLine);
          layer.htmlLayer.add(heightHtmlElement);
        } else if (this.middlePanel.options.type === Panel.types.CIRCLE) {
          const { object: circleObject, htmlElement: circleHtmlElement } = this.screwGroup.createScrewCircle(options);
          if (this.is2DMode) {
            circleHtmlElement.position.x -= this.panels.position.z;
            circleHtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
          } else {
            circleHtmlElement.position.set(
              this.panels.position.x + this.middlePanel.size.depth / 2,
              this.panels.position.y - circleHtmlElement.position.x + 20,
              this.panels.position.z
            );
            circleHtmlElement.optimalCameraDist = 3000;
            circleHtmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              circleHtmlElement.setDirection(
                circleHtmlElement.position.clone().setZ(circleHtmlElement.position.z + this.middlePanel.size.width / 2),
                circleHtmlElement.position.clone().setZ(circleHtmlElement.position.z - this.middlePanel.size.width / 2),
                false
              );
            }
            circleHtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
          }
          this.middlePanel.object.addMesh(circleObject);
          layer.htmlLayer.add(circleHtmlElement);
        }

        if (this.middlePanel.options.type === Panel.types.SQUARE) {
          if (this.options.screw.count.n > 1) {
            // Create S1 label
            const { object: s1Object, htmlElement: s1HtmlElement } = this.middlePanel.createS1Label(
              this.screwGroup, 
              false, 
              LabelsPadding, 
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options
            );
            if (s1HtmlElement) {
              if (this.is2DMode) {
                s1HtmlElement.position.x -= this.panels.position.z;
                s1HtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
              } else {
                s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
                s1HtmlElement.position.set(
                  this.panels.position.x + this.middlePanel.size.depth / 2,
                  this.panels.position.y - s1HtmlElement.position.z,
                  this.panels.position.z
                );
                s1HtmlElement.optimalCameraDist = 3000;
                s1HtmlElement.zoomFactor = 0.5;
                if (!options.displayHorizontally) {
                  s1HtmlElement.setDirection(
                    s1HtmlElement.position.clone().setZ(s1HtmlElement.position.z + this.screwGroup.options.padding.s1 / 2),
                    s1HtmlElement.position.clone().setZ(s1HtmlElement.position.z - this.screwGroup.options.padding.s1 / 2),
                    false
                  );
                }
              }
              this.middlePanel.object.addMesh(s1Object);
              layer.htmlLayer.add(s1HtmlElement);
            }
          }

          if (this.options.screw.count.m > 1) {
            // Create S2 label
            const { object: s2Object, htmlElement: s2HtmlElement } = this.middlePanel.createS2Label(
              this.screwGroup, 
              true, 
              this.middlePanel.size.width / 2 + LabelsPadding, 
              this.middlePanel.size.height / 2 - this.middlePanel.options.dy.value,
              options
            );
            if (s2HtmlElement && s2Object) {
              if (this.is2DMode) {
                s2HtmlElement.position.x -= this.panels.position.z;
                s2HtmlElement.position.z -= this.panels.position.y + panelHTMLLabelsCorrection;
              } else {
                s2HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
                s2HtmlElement.position.set(
                  this.panels.position.x + this.middlePanel.size.depth / 2,
                  this.panels.position.y -
                    (this.options.panel.type === Panel.types.CIRCLE ? this.middlePanel.size.width / 2 : this.middlePanel.size.height / 2) 
                    + this.middlePanel.options.dy.value + this.screwGroup.options.padding.s2 / 2 - panelHTMLLabelsCorrection,
                  this.panels.position.z + this.middlePanel.size.width / 2 + LabelsPadding
                );
                s2HtmlElement.optimalCameraDist = 3000;
                s2HtmlElement.zoomFactor = 0.5;
                if (!options.displayHorizontally) {
                  s2HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
                  s2HtmlElement.position.z += 15;
                  s2HtmlElement.setDirection(
                    s2HtmlElement.position.clone().setY(s2HtmlElement.position.y + this.screwGroup.options.padding.s2 / 2),
                    s2HtmlElement.position.clone().setY(s2HtmlElement.position.y - this.screwGroup.options.padding.s2 / 2),
                    false
                  );
                } else {
                  s2HtmlElement.align = HTMLEditableLabel.types.align.RIGHT;
                }
              }
              this.middlePanel.object.addMesh(s2Object);
              layer.htmlLayer.add(s2HtmlElement);
            }
          }
        } else if (this.middlePanel.options.type === Panel.types.BEND && Array.isArray(this.screwGroup.screwCoordinates[1])) {
          // Create S1 label
          const { object: s1Object, htmlElement: s1HtmlElement } = this.middlePanel.createS1Label(
            this.screwGroup,
            false, 
            (this.screwGroup.screwCoordinates[1].length > 1 ? LabelsPadding : LabelsPadding /2), 
            this.middlePanel.size.height / 2 - (this.screwGroup.screwCoordinates[1].length > 1 ? this.middlePanel.options.dy.value : 0),
            options);
          if (s1HtmlElement && s1Object) {
            if (this.is2DMode) {
              s1HtmlElement.position.x -= this.panels.position.z;
              s1HtmlElement.position.z -= this.panels.position.y;
            } else {
              if (this.screwGroup.screwCoordinates[1].length > 1) {
                s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
                s1HtmlElement.position.set(
                  this.panels.position.x + this.middlePanel.size.depth / 2,
                  this.panels.position.y - s1HtmlElement.position.z,
                  this.panels.position.z
                );
              } else {
                s1HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
                s1HtmlElement.position.set(
                  this.panels.position.x + s1HtmlElement.position.y,
                  this.panels.position.y - s1HtmlElement.position.z + LabelsPadding /2,
                  this.panels.position.z
                );
              }
              s1HtmlElement.optimalCameraDist = 3000;
              s1HtmlElement.zoomFactor = 0.5;
              if (!options.displayHorizontally) {
                s1HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
                s1HtmlElement.setDirection(
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.y + this.screwGroup.options.padding.s1 / 2),
                  s1HtmlElement.position.clone().setZ(s1HtmlElement.position.y - this.screwGroup.options.padding.s1 / 2),
                  false
                );
              }
            }
            this.middlePanel.object.addMesh(s1Object);
            layer.htmlLayer.add(s1HtmlElement);
          }
        }

        if (this.is3DMode && this.options.panel.type === Panel.types.BEND) {
          // Create lyh label
          const lyh = this.middlePanel.createLyhLabel(this.options.object.dimensions);
          this.layer3D.addMesh(lyh.object);
          lyh.htmlElement.optimalCameraDist = 3000;
          lyh.htmlElement.zoomFactor = 0.5;
          this.layer3D.htmlLayer.add(lyh.htmlElement);
        }
        break;
      }
      default: {
        break;
      }
    }

    this.createShiftLabels(layer, options);

  }

  setOrientation() {
    super.setOrientation();

    if (this.is2DMode) {
      switch (this.options.panel.position) {
        case Panel.positions.HORIZONTAL_X: {
          this.layer2D.mesh.rotation.x = 0;
          this.layer2D.mesh.rotation.y = 0;
          this.layer2D.mesh.rotation.z = 0;
          break;
        }
        case Panel.positions.VERTICAL_X: {
          this.layer2D.mesh.rotation.x = 0;
          this.layer2D.mesh.rotation.y = Math.PI / -2;
          this.layer2D.mesh.rotation.z = Math.PI / 2;
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  createGround() {
    this.options.ground.depth = 1000;

    this.ground = new Ground(this.options.ground);
    this.ground.onOptionUpdate(this.dispatchOptionUpdate);
    this.ground.mesh.rotation.y = Math.PI;
    this.ground.mesh.rotation.x = Math.PI / 2;
  }

  createC1yLabel(startY, options) {
    const { left } = this.screwPositions;

    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          x: this.panels.position.x + startY,
          y: this.panels.position.y + this.middlePanel.options.depth / 2,
          z: this.panels.position.z + left.y//bottom.z
        },
        to: {
          x: this.panels.position.x + startY,
          y: this.panels.position.y + this.middlePanel.options.depth / 2,
          z: this.panels.position.z + left.y + (this.ground.options.padding.C1x || 0) + LabelsPadding,
        }
      },
      bottom: {
        from: {
          x: this.panels.position.x + startY + this.ground.options.padding.C1y,
          y: this.panels.position.y + this.middlePanel.options.depth / 2,
          z: this.panels.position.z + 
            (this.ground.options.padding.C1y ? left.y + (this.ground.options.padding.C1x || 0) : 0)
        },
        to: {
          x: this.panels.position.x + startY + this.ground.options.padding.C1y,
          y: this.panels.position.y + this.middlePanel.options.depth / 2,
          z: this.panels.position.z + 
          (this.ground.options.padding.C1y ? (left.y + (this.ground.options.padding.C1x || 0) + LabelsPadding) : 0)
        }
      },
      size: this.ground.options.padding.C1y || 0,
      arrowDirection: Arrow.types.direction.X,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `C1y = ` : "") +
        `${unitTransform(+this.ground.options.padding.C1y).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.ground.options.padding.C1y).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            padding: {
              C1y: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      }
    });
    
    if (this.is3DMode) {
      htmlElement.position.set(
        -htmlElement.position.x,
        this.panels.position.y + this.middlePanel.options.depth / 2,
        this.panels.position.z + 
          (this.ground.options.padding.C1y ? (left.y + (this.ground.options.padding.C1x || 0) + LabelsPadding) : 0)
      );

      htmlElement.optimalCameraDist = 3000;
      htmlElement.zoomFactor = 0.5;
      if (options && !options.displayHorizontally) {
        htmlElement.align = HTMLEditableLabel.types.align.CENTER;
        htmlElement.position.z += 15;
        htmlElement.setDirection(
          htmlElement.position.clone().setX(htmlElement.position.x + Math.max(25, this.ground.options.padding.C1y || 0)),
          htmlElement.position.clone().setX(htmlElement.position.x - Math.max(25, this.ground.options.padding.C1y || 0)),
          false
        );
      }
    } else {
      line.rotation.y = -Math.PI / 2;
      htmlElement.position.set(
        -htmlElement.position.y,
        htmlElement.position.z,
        -htmlElement.position.x
      );
    }

    return {
      line,
      htmlElement
    };
  }

  createC1xLabel(startY, options) {
    const { left } = this.screwPositions;

    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          x: this.panels.position.x + startY,
          y: this.panels.position.y + this.middlePanel.options.depth / 2,
          z: this.panels.position.z + left.y
        },
        to: {
          x: this.panels.position.x + startY + (this.options.ground.padding.C1y || 0) + LabelsPadding,
          y: this.panels.position.y + this.middlePanel.options.depth / 2,
          z: this.panels.position.z + left.y 
        }
      },
      bottom: {
        from: {
          x: this.panels.position.x + startY + (this.options.ground.padding.C1y || 0),
          y: this.panels.position.y + this.middlePanel.options.depth / 2,
          z: this.panels.position.z + (this.ground.options.padding.C1x ? (left.y + (this.ground.options.padding.C1x || 0)) : 0)
        },
        to: {
          x: this.panels.position.x + startY + (this.options.ground.padding.C1y || 0) + LabelsPadding,
          y: this.panels.position.y + this.middlePanel.options.depth / 2,
          z: this.panels.position.z + (this.ground.options.padding.C1x ? (left.y + (this.ground.options.padding.C1x || 0)) : 0)
        }
      },
      size: this.ground.options.padding.C1x || 0,
      arrowDirection: Arrow.types.direction.Z,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `C1x = ` : "") +
        `${unitTransform(+this.ground.options.padding.C1x).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.ground.options.padding.C1x).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.TOP,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            padding: {
              C1x: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      }
    });

    if (this.is3DMode) {
      htmlElement.position.set(
        this.panels.position.x + startY + (this.options.ground.padding.C1y || 0) + LabelsPadding,
        this.panels.position.y + this.middlePanel.options.depth / 2,
        htmlElement.position.z
      );
      
      htmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      htmlElement.optimalCameraDist = 3000;
      htmlElement.zoomFactor = 0.5;
      if (options && !options.displayHorizontally) {
        htmlElement.align = HTMLEditableLabel.types.align.CENTER;
        htmlElement.position.x += 15;
        htmlElement.setDirection(
          htmlElement.position.clone().setZ(htmlElement.position.z + Math.max(25, this.ground.options.padding.C1x || 0)),
          htmlElement.position.clone().setZ(htmlElement.position.z - Math.max(25, this.ground.options.padding.C1x || 0)),
          false
        );
      }
    } else {
      line.rotation.y = -Math.PI / 2;
      htmlElement.position.set(
        -htmlElement.position.z,
        htmlElement.position.x,
        htmlElement.position.y
      );
    }

    return {
      line,
      htmlElement
    };
  }

  get DPP() {
    return this.options.panel.dpp.active ? this.options.panel.dpp.value : 0;
  }

  createDppLabel(parent, options) {
    const panelHeight = this.options.panel.type === Panel.types.CIRCLE ? this.options.panel.width : this.options.panel.height;
    const arrow = new Arrow({
      size: -this.options.panel.dpp.value,
      position: {
        x: 0,
        y: -this.options.panel.width / 2 - LabelsPadding,
        z: -panelHeight / 2
      },
      direction: Arrow.types.direction.Z,
      disableDepthTest: true
    });

    parent.object.addObject(arrow);
    if (this.options.panel.type === Panel.types.CIRCLE) {
      const bottomLine = new Line({
        from: {
          x: 0,
          y: 0,
          z: -this.options.panel.width / 2
        },
        to: {
          x: 0,
          y: -this.options.panel.width / 2 - LabelsPadding,
          z: -this.options.panel.width / 2
        },
        disableDepthTest: true
      });
      parent.object.addObject(bottomLine);
    }

    const htmlElement = new HTMLEditableLabel({
      text: ((!options || !options.is3DMode || options.showLabels) ? 'Dpp = ' : '') + 
        `${unitTransform(+this.options.panel.dpp.value).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.options.panel.dpp.value).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        panel: {
          dpp: {
            value: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      -this.panels.position.z + this.options.panel.width / 2 + LabelsPadding,
      this.panels.position.x,
      -this.panels.position.y - panelHeight / 2 - this.options.panel.dpp.value / 2
    );

    return {
      arrow,
      htmlElement
    };
  }

  createDplLabel(position, options) {
    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          x: position.x, 
          y: position.y,
          z: position.z
        },
        to: {
          x: position.x, 
          y: position.y,
          z: position.z
        }
      },
      bottom: {
        from: {
          x: position.x, 
          y: position.y - LabelsPadding,
          z: position.z + this.options.panel.dpl.value
        },
        to: {
          x: position.x, 
          y: position.y,
          z: position.z + this.options.panel.dpl.value
        }
      },
      size: this.options.panel.dpl.value || 0,
      arrowDirection: Arrow.types.direction.Z,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? 'Dpl = ' : '') + 
      `${unitTransform(+this.options.panel.dpl.value).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.options.panel.dpl.value).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          panel: {
            dpl: {
              value: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      }
    });

    return {
      line,
      htmlElement
    };
  }

  createShiftLabels(layer, options) {
    var shiftLabels = null;
    switch (this.options.panel.position) {
      case Panel.positions.HORIZONTAL_X: {
        shiftLabels = this.barrier.createShiftLabels(
          this.dispatchOptionUpdate, 
          this.options.beam.shift, 
          new THREE.Vector3(-this.panels.position.z, -this.panels.position.x, this.panels.position.y),
          options
        );

        if (shiftLabels) {
          if (shiftLabels.marker) {
            shiftLabels.marker.position.set(
              this.panels.position.x, 
              this.panels.position.y + this.middlePanel.size.depth / 2, 
              this.panels.position.z
            );
            layer.addObject(shiftLabels.marker);
          }
          if (shiftLabels.xLabel) {
            if (options && options.is3DMode) {
              shiftLabels.xLabel.object.rotation.set(0, 1.57, 1.57, "ZXY");
              shiftLabels.xLabel.object.position.y += this.middlePanel.size.depth / 2;
              shiftLabels.xLabel.HTMLObject.verticalAlign = HTMLEditableLabel.types.align.CENTER;
              shiftLabels.xLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.xLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.xLabel.HTMLObject.position.set(
                shiftLabels.xLabel.HTMLObject.position.z + (this.options.beam.shift.y < 0 ? -20 : 20),
                shiftLabels.xLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
                -shiftLabels.xLabel.HTMLObject.position.x,
              );
              if (!options.displayHorizontally) {
                shiftLabels.xLabel.HTMLObject.setDirection(
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z + Math.max(25, this.options.beam.shift.x / 2)
                  ), 
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z - Math.max(25, this.options.beam.shift.x / 2)
                  ),
                  false
                );
              }
            } else {
              shiftLabels.xLabel.object.rotation.set(-1.57, 1.57, 1.57, "ZXY");
            }
            layer.addMesh(shiftLabels.xLabel.object);
            layer.htmlLayer.add(shiftLabels.xLabel.HTMLObject);
          }
          if (shiftLabels.yLabel) {
            shiftLabels.yLabel.object.rotation.set(-1.57, 1.57, 1.57, "ZXY");
            if (options && options.is3DMode) {
              shiftLabels.yLabel.object.rotation.set(0, 1.57, 1.57, "ZXY");
              shiftLabels.yLabel.object.position.y += this.middlePanel.size.depth / 2;
              shiftLabels.yLabel.HTMLObject.align = HTMLEditableLabel.types.align.CENTER;
              shiftLabels.yLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.yLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.yLabel.HTMLObject.position.set(
                shiftLabels.yLabel.HTMLObject.position.z,
                shiftLabels.yLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
                -shiftLabels.yLabel.HTMLObject.position.x + (this.options.beam.shift.x < 0 ? -20 : 20),
              );
              if (!options.displayHorizontally) {
                shiftLabels.yLabel.HTMLObject.setDirection(
                  shiftLabels.yLabel.HTMLObject.position.clone().setX(
                    shiftLabels.yLabel.HTMLObject.position.x + Math.max(25, this.options.beam.shift.y / 2)
                  ), 
                  shiftLabels.yLabel.HTMLObject.position.clone().setX(
                    shiftLabels.yLabel.HTMLObject.position.x - Math.max(25, this.options.beam.shift.y / 2)
                  ),
                  false
                );
              }
            }
            layer.addMesh(shiftLabels.yLabel.object);
            layer.htmlLayer.add(shiftLabels.yLabel.HTMLObject);
          }
        }
        break;
      }
      case Panel.positions.VERTICAL_X: {
        shiftLabels = this.barrier.createShiftLabels(
          this.dispatchOptionUpdate, 
          this.options.beam.shift, 
          new THREE.Vector3(-this.panels.position.z, this.panels.position.y, this.panels.position.x),
          options
        );
        
        if (shiftLabels) {
          if (shiftLabels.marker) {
            shiftLabels.marker.rotation.z = Math.PI / 2;
            shiftLabels.marker.position.set(
              this.panels.position.x + this.middlePanel.size.depth / 2, 
              this.panels.position.y, 
              this.panels.position.z
            );
            layer.addObject(shiftLabels.marker);
          }
          if (shiftLabels.xLabel) {
            shiftLabels.xLabel.object.rotation.y = Math.PI / 2;
            if (options && options.is3DMode) {
              shiftLabels.xLabel.object.position.x += this.middlePanel.size.depth / 2;
              shiftLabels.xLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.xLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.xLabel.HTMLObject.verticalAlign = this.options.beam.shift.y < 0 ? 
                HTMLEditableLabel.types.verticalAlign.BOTTOM : HTMLEditableLabel.types.verticalAlign.TOP;
              shiftLabels.xLabel.HTMLObject.position.set(
                shiftLabels.xLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
                -shiftLabels.xLabel.HTMLObject.position.z, // + (this.options.beam.shift.y < 0 ? 20 : -20),
                -shiftLabels.xLabel.HTMLObject.position.x,
              );
              if (!options.displayHorizontally) {
                shiftLabels.xLabel.HTMLObject.setDirection(
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z + Math.max(25, this.options.beam.shift.x / 2)
                  ), 
                  shiftLabels.xLabel.HTMLObject.position.clone().setZ(
                    shiftLabels.xLabel.HTMLObject.position.z - Math.max(25, this.options.beam.shift.x / 2)
                  ),
                  false
                );
              }
            }
            layer.addMesh(shiftLabels.xLabel.object);
            layer.htmlLayer.add(shiftLabels.xLabel.HTMLObject);
          }
          if (shiftLabels.yLabel) {
            shiftLabels.yLabel.object.rotation.y = Math.PI / 2;
            if (options && options.is3DMode) {
              shiftLabels.yLabel.object.position.x += this.middlePanel.size.depth / 2;
              shiftLabels.yLabel.HTMLObject.optimalCameraDist = 3000;
              shiftLabels.yLabel.HTMLObject.zoomFactor = 0.5;
              shiftLabels.yLabel.HTMLObject.align = HTMLEditableLabel.types.align.CENTER;
              shiftLabels.yLabel.HTMLObject.verticalAlign = HTMLEditableLabel.types.align.CENTER;
              shiftLabels.yLabel.HTMLObject.position.set(
                shiftLabels.yLabel.HTMLObject.position.y + this.middlePanel.size.depth / 2,
                -shiftLabels.yLabel.HTMLObject.position.z,
                -shiftLabels.yLabel.HTMLObject.position.x + (this.options.beam.shift.x < 0 ? -20 : 20),
              );
              if (!options.displayHorizontally) {
                shiftLabels.yLabel.HTMLObject.setDirection(
                  shiftLabels.yLabel.HTMLObject.position.clone().setY(
                    shiftLabels.yLabel.HTMLObject.position.y + Math.max(25, this.options.beam.shift.y / 2)
                  ), 
                  shiftLabels.yLabel.HTMLObject.position.clone().setY(
                    shiftLabels.yLabel.HTMLObject.position.y - Math.max(25, this.options.beam.shift.y / 2)
                  ),
                  false
                );
              }
            }
            layer.addMesh(shiftLabels.yLabel.object);
            layer.htmlLayer.add(shiftLabels.yLabel.HTMLObject);
          }
        }
        break;
      }
      default:
        break;
    }
    
  }

}



// WEBPACK FOOTER //
// ./src/object-view/generators/safety-barrier/objects/stairs.js